import type React from 'react';

import format from 'date-fns/format';
import parse from 'date-fns/parse';
import throttle from 'lodash/throttle';
import { useCallback, useMemo } from 'react';

import useInputState from '$/components/elements/Inputs/hooks/useInputState';
import useModal from '$/hooks/useModal';
import { DateTypes } from '$/types/enums/DateTypes';
import isDateValid from '$/utils/isDateValid';

const checkDate = (
  date: string | Date | null | undefined
): Date | undefined => {
  if (!date) return undefined;

  try {
    const _date = new Date(date);
    return isDateValid(_date) ? _date : undefined;
  } catch (e) {
    return undefined;
  }
};

const checkIfStringIsDateFormat = (dateString?: string | null) => {
  if (!dateString) {
    return false;
  }
  return dateString
    .split('.')
    .reduce((prev, curr) => prev && !isNaN(Number(curr)), true);
};

export interface IUseDateInput {
  value: Date;
  onChange: (date: Date | null) => void;
  dateFormat?: DateTypes;
  disabled?: boolean;
}

const useDateInput = ({
  value,
  onChange,
  dateFormat = DateTypes.MONTH_YEAR_DOT,
  disabled = false,
}: IUseDateInput) => {
  const throttledChangeHandler = useMemo(
    () => throttle(onChange, 200),
    [onChange]
  );
  const [datePickerValue, setDatePickerValue] = useInputState<Date | null>(
    checkDate(value) ?? null
  );
  const [textInputValue, setTextInputValue] = useInputState<string>(
    (() => {
      const _date = checkDate(value);
      if (!_date) return '';
      return format(_date, dateFormat as string);
    })()
  );
  const [isModalOpen, openModal, closeModal, dialogRef] = useModal({
    disabled,
  });

  const handleDatePickerOnChange = useCallback<
    (options?: { closeModalOnChange?: boolean }) => (date: Date) => void
  >(
    ({ closeModalOnChange = true } = {}) =>
      (date) => {
        setDatePickerValue(date ?? null);
        throttledChangeHandler(date ?? undefined);

        if (closeModalOnChange) {
          closeModal();
        }
      },
    [closeModal, setDatePickerValue, throttledChangeHandler]
  );

  const handleTextInputOnChange = useCallback<
    React.FormEventHandler<HTMLInputElement>
  >(
    (e) => {
      // @ts-ignore
      const _newString = e.target.value as string;
      setTextInputValue(_newString);
      if (!checkIfStringIsDateFormat(_newString)) {
        return;
      }
      throttledChangeHandler(parse(_newString, dateFormat, new Date()));
      closeModal();
    },
    [dateFormat, setTextInputValue, throttledChangeHandler, closeModal]
  );

  return {
    datePickerValue,
    textInputValue,
    isModalOpen,
    openModal,
    closeModal,
    dialogRef,
    handleDatePickerOnChange,
    handleTextInputOnChange,
  };
};

export default useDateInput;
